import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Icon } from '@PetSureAustralia/petsure-react-ui-library/distLib/components';
import { useSearchParams } from 'react-router-dom';
import { SLICES } from '../../../constants/slices';
import { PetSelection } from '../../../components/claims/newClaim/newClaimSteps/petSelection';
import { GetPetDetailsFromPolicyNumber } from '../../../helpers/petHelpers';
import { PaymentDetailsPanel } from '../../../components/payments/paymentDetailsPanel/paymentDetailsPanel';
import { ReimbursementDetailsPanel } from '../../../components/payments/reimbursementDetailsPanel/reimbursementDetailsPanel';
import { useGetPetInfoQuery } from '../../../data/policy';
import { SuccessMessageDialog } from '../../../components/successMessage/successMessageDialog';
import { ReimbursementPopup } from '../../../components/payments/reimbursementPopup/reimbursementPopup';
import { PremiumPaymentPopup } from '../../../components/payments/premiumPaymentPopup/premiumPaymentPopup';
import Loading from '../../../components/shared/Loading/Loading';
import { singlePetSuccessMessage } from '../../../utils/successMessages';
import { SUCCESS_MESSAGES } from '../../../constants/constants';
import { useValidateStateWithBankingInfoMutation } from '../../../data/customer';
import { handleTracking } from '../../../services/gtmService/tracking';

export const BankDetails = () => {
  // Using the RTK query hook for getting pet info (only triggered when token is available)
  const { authDetails } = useSelector((state) => state?.[SLICES.AUTHENTICATION]);
  const { auth0AccessToken } = authDetails;
  const { data: petDetails, isLoading: petDetailsLoading } = useGetPetInfoQuery(auth0AccessToken, {
    skip: !auth0AccessToken
  });
  const [selectedPet, setSelectedPet] = useState();
  const [isBankDetailsPopupActive, setIsBankDetailsPopupActive] = useState(false);
  const [isPremiumPaymentPopUpActive, setIsPremiumPaymentPopUpActive] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState({ active: false, isApplyToAllPetChecked: false });
  const [searchParams] = useSearchParams();
  const fullPolicyNumber = searchParams.get('FullPolicyNumber'); // Get FullPolicyNumber from URL
  const [validateStateWithBankingInfo, { data: validateResponse }] = useValidateStateWithBankingInfoMutation();
  // Set selectedPet once petDetails is loaded
  useEffect(() => {
    if (petDetails) {
      const defaultSelected = fullPolicyNumber
        ? petDetails?.find((pet) => pet.FullPolicyNumber === fullPolicyNumber)
        : petDetails[0];
      setSelectedPet(defaultSelected);
    }
  }, [petDetails, fullPolicyNumber]); // Trigger when petDetails or activePet changes

  useEffect(() => {
    if (window.location.search.includes('payment=cancelled')) {
      handleTracking('payment_cancelled', { label: 'Bank Details' });
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    if (!window.location.search.includes('userState=')) return;

    const queryString = window.location.search;

    validateStateWithBankingInfo({
      QueryString: queryString
    });

    window.history.replaceState({}, '', window.location.pathname);
  }, [validateStateWithBankingInfo]);

  useEffect(() => {
    if (validateResponse?.responseObject === 'Registered') {
      setShowSuccessMessage({ active: true, isApplyToAllPetChecked: false });
    }
  }, [validateResponse]);

  const handlePetSelection = (selection) => {
    setSelectedPet(
      GetPetDetailsFromPolicyNumber({
        petDetails,
        policyNumber: selection.policyNumber
      })
    );
  };

  // TODO: Premium payment functionality
  const handlePaymentUpdate = () => {
    if (!petDetailsLoading) {
      setIsPremiumPaymentPopUpActive(true);
    }
  };

  const handleReimbursementUpdate = () => {
    if (!petDetailsLoading) {
      setIsBankDetailsPopupActive(true);
    }
  };

  return (
    <main
      className="premiumsAndPayments"
      aria-label="My Bank Details page"
    >
      <h1 className="mainPageHeader">Premiums &amp; payments</h1>

      <h2>My bank details</h2>
      {petDetailsLoading || !selectedPet ? (
        <Loading />
      ) : (
        <PetSelection
          petDetails={petDetails}
          onPetSelection={handlePetSelection}
          activePolicyNumber={selectedPet?.PolicyNumber}
        />
      )}

      <div className="paymentDetailsPanel">
        <header>
          <h3>
            Premium payment details
            <small>This is your premium payment method.</small>
          </h3>
          <button
            className="button primary outline"
            type="button"
            onClick={handlePaymentUpdate}
          >
            <Icon icon="pencil" />
            <span className="labelText">Update</span>
            <span className="sr-only">premium payment details</span>
          </button>
        </header>
        {petDetailsLoading || !selectedPet ? <Loading /> : <PaymentDetailsPanel selectedPet={selectedPet} />}
      </div>

      <div className="paymentDetailsPanel reimbursementDetailsPanel">
        <header>
          <h3>
            Claim reimbursement details
            <small>This is your claim payment method where we will deposit your successful claim payments.</small>
          </h3>
          <button
            className="button primary outline"
            type="button"
            onClick={handleReimbursementUpdate}
          >
            <Icon icon="pencil" />
            <span className="labelText">Update</span>
            <span className="sr-only">reimbursement payment details</span>
          </button>
        </header>
        {petDetailsLoading || !selectedPet ? <Loading /> : <ReimbursementDetailsPanel selectedPet={selectedPet} />}
      </div>

      {isBankDetailsPopupActive && (
        <ReimbursementPopup
          activePet={selectedPet}
          onCancel={() => setIsBankDetailsPopupActive(false)}
          setShowSuccessMessage={setShowSuccessMessage}
          isMyProfileReimbusementUpdate={false}
        />
      )}

      {isPremiumPaymentPopUpActive && (
        <PremiumPaymentPopup
          activePet={selectedPet}
          petDetails={petDetails}
          onCancel={() => setIsPremiumPaymentPopUpActive(false)}
          setShowSuccessMessage={setShowSuccessMessage}
        />
      )}

      {showSuccessMessage.active && (
        <SuccessMessageDialog
          message={`${singlePetSuccessMessage()}${
            showSuccessMessage.isApplyToAllPetChecked ? SUCCESS_MESSAGES.MULTI_PET : ''
          }`}
          onClose={() => setShowSuccessMessage(false)}
        />
      )}
    </main>
  );
};
